/**
 * TempEmailBanner.tsx
 *
 * 仮メールアドレス（@mangaos.test / 自動生成の @assistant.local）のまま使っているアシスタントへの、本メール登録の案内。
 *
 * 構成:
 *   - 常時バナー（黄色細帯）: ログインするたびに表示、×で今セッションだけ非表示
 *   - スポットライトカード（青カード）: login_count が 1 または 3 の倍数の場合に表示
 *
 * 登録・確認の操作は、「ログイン・セキュリティ」のステップ式の画面（確認コード付き）に一本化している。
 * （以前はここに、確認コードなしでメールアドレスを書き換える専用のダイアログがあった。入口が二重になるため廃止し、案内だけにした）
 *
 * 操作を止めない: 案内のみ。通常操作は完全に可能。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthGuard'
import { useIsMobile } from '../hooks/useIsMobile'

// sessionStorage キー（タブを閉じると消えるのでログイン毎にリセット）
const BANNER_KEY    = 'mangaos-temp-email-banner-hidden'
const SPOTLIGHT_KEY = 'mangaos-temp-email-spotlight-hidden'

/** サーバーの isPlaceholderEmail と同じ判定（@mangaos.test と、自動生成の @assistant.local） */
function isTempEmail(email: string) {
  const e = email.toLowerCase().trim()
  return e.endsWith('@mangaos.test') || e.endsWith('@assistant.local')
}

const SECURITY_PATH = '/account/security'

// ── 常時バナー（細帯）────────────────────────────────────────────
function PersistentBanner({ onOpen, onDismiss }: {
  onOpen:    () => void
  onDismiss: () => void
}) {
  // ★ スマホ幅では横並びだと「＋話数追加」ボタン等と同じ幅衝突で本文が見切れるため、
  //   縦積みレイアウトに切り替える（PC幅は既存の横並びレイアウトのまま）。
  const isMobile = useIsMobile()

  return (
    <div style={{
      background: '#fffbeb',
      borderBottom: '1px solid #fde68a',
      padding: isMobile ? '10px 14px' : '7px 16px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      gap: isMobile ? 8 : 12,
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      fontSize: 12,
      boxSizing: 'border-box',
      width: '100%',
      overflow: 'visible',
    }}>
      <span style={{
        flex: isMobile ? 'none' : 1,
        minWidth: isMobile ? 0 : 200,
        color: 'var(--ink-2)',
        lineHeight: 1.5,
        display: 'block',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        maxWidth: '100%',
      }}>
        <strong style={{ marginRight: 6, display: isMobile ? 'block' : 'inline', marginBottom: isMobile ? 2 : 0 }}>
          📧 本メールアドレスの登録をお願いします
        </strong>
        <span style={{ color: 'var(--ink-3)' }}>
          現在、仮メールアドレスでログインしています。確定シフト・給与のお知らせを受け取るため、本人のメールアドレスを登録してください（登録したアドレスが、次回からのログインIDになります）。
        </span>
      </span>
      <div style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        justifyContent: isMobile ? 'space-between' : 'flex-start',
        flexShrink: 0,
        width: isMobile ? '100%' : undefined,
      }}>
        <button
          className="btn btn-sm btn-outline"
          onClick={onOpen}
          style={{ fontSize: 11, whiteSpace: 'nowrap', padding: '3px 10px' }}
        >
          メールアドレスを登録・確認する
        </button>
        <button
          onClick={onDismiss}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--ink-3)', fontSize: 18, lineHeight: 1, padding: '0 4px',
            flexShrink: 0,
          }}
          aria-label="案内を閉じる"
          title="今回のログイン中は非表示"
        >
          ×
        </button>
      </div>
    </div>
  )
}

// ── 3回ごとスポットライトカード ──────────────────────────────────
function SpotlightCard({ onRegister, onDismiss }: {
  onRegister: () => void
  onDismiss:  () => void
}) {
  // ★ PersistentBanner と同じ理由でスマホ幅は縦積みレイアウトに切り替える
  const isMobile = useIsMobile()

  return (
    <div style={{
      background: '#f0f9ff',
      border: '1px solid #bae6fd',
      borderRadius: 8,
      padding: isMobile ? '12px 14px' : '14px 18px',
      marginBottom: 16,
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 10 : 16,
      alignItems: isMobile ? 'stretch' : 'center',
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      boxSizing: 'border-box',
      width: '100%',
      overflow: 'visible',
    }}>
      <div style={{ flex: isMobile ? 'none' : 1, minWidth: isMobile ? 0 : 200, maxWidth: '100%' }}>
        <div style={{
          fontWeight: 700, fontSize: 14, marginBottom: 5, color: '#0369a1',
          whiteSpace: 'normal', wordBreak: 'break-word',
        }}>
          📬 本メールアドレスの登録はお済みですか？
        </div>
        <div style={{
          fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.7,
          whiteSpace: 'normal', wordBreak: 'break-word',
        }}>
          現在は仮メールアドレスでログインしています。<br />
          確定シフトや給与関連のお知らせを受け取るため、本人のメールアドレス登録をお願いします。登録したアドレスが次回からのログインIDになります。
        </div>
      </div>
      <div style={{
        display: 'flex', gap: 8, flexShrink: 0,
        width: isMobile ? '100%' : undefined,
      }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={onRegister}
          style={{ fontSize: 12, whiteSpace: 'nowrap', flex: isMobile ? 1 : undefined }}
        >
          今すぐ登録する
        </button>
        <button
          className="btn btn-outline btn-sm"
          onClick={onDismiss}
          style={{ fontSize: 12, flex: isMobile ? 1 : undefined }}
        >
          あとで
        </button>
      </div>
    </div>
  )
}

// ── メイン：ページとページの間（TopBar直下）に挿入するバナー ─────
export function TempEmailBanner() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [bannerDismissed, setBannerDismissed] = useState(() =>
    sessionStorage.getItem(BANNER_KEY) === '1'
  )

  // 仮メールでなければ何も表示しない
  if (!user || !isTempEmail(user.email)) return null

  const dismissBanner = () => {
    sessionStorage.setItem(BANNER_KEY, '1')
    setBannerDismissed(true)
  }

  // 常時バナー（TopBar直下の細帯）。登録・確認は「ログイン・セキュリティ」へ
  return bannerDismissed ? null : (
    <PersistentBanner onOpen={() => navigate(SECURITY_PATH)} onDismiss={dismissBanner} />
  )
}

// ── ページコンテンツ上部に差し込むスポットライトカード ──────────
// AssistantReports など各ページの一番上に置くことで、
// ページコンテンツとしてスクロールされる（ブロックしない）。
export function TempEmailSpotlight() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(SPOTLIGHT_KEY) === '1'
  )

  if (!user || !isTempEmail(user.email) || dismissed) return null

  const loginCount = user.login_count ?? 0
  if (loginCount !== 1 && loginCount % 3 !== 0) return null

  const dismiss = () => {
    sessionStorage.setItem(SPOTLIGHT_KEY, '1')
    setDismissed(true)
  }

  return (
    <SpotlightCard
      onRegister={() => { dismiss(); navigate(SECURITY_PATH) }}
      onDismiss={dismiss}
    />
  )
}
